import courseJson from '../../shared/course.json'

export type LonLat = [number, number]

export type CourseMarker = { kind: 'km' | 'mile'; label: string; atM: number; pos: LonLat }
export type CoursePoi = {
  kind: 'start' | 'turnaround' | 'water' | 'crossing' | 'bridge' | 'junction' | 'grove'
  label: string
  /** Groves: what happens there on race morning */
  role?: string
  atM: number[]
  pos: LonLat
  note: string
}

export type Course = {
  attribution: string
  generatedAt: string
  layout: string
  distanceM: number
  legM: number
  redLoopM: number
  surface: Record<string, number>
  elevation: { minM: number; maxM: number; gainM: number; profile: [number, number][] }
  route: LonLat[]
  /** What the out-leg runs on, in order (trail, park road, …) */
  segments: { fromM: number; toM: number; on: string }[]
  markers: CourseMarker[]
  pois: CoursePoi[]
  context: {
    lake: LonLat[][]
    lakeIslands: LonLat[][]
    redLoop: LonLat[]
    spurs: LonLat[][]
    preserve: LonLat[][]
    /** Woods and fields outside the preserve outline (north of Higgins Road) */
    green: LonLat[][]
    streams: LonLat[][]
    roads: { label: string; line: LonLat[] }[]
    parking: LonLat[][]
  }
}

export const course = courseJson as unknown as Course

/** Meters to whole feet, for US readers. */
export const feet = (m: number) => Math.round(m * 3.28084)

/** Simple local projection: good to well under 1% over a few kilometers. */
export function makeProjection(bounds: [LonLat, LonLat], width: number, pad = 0) {
  const [[minLon, minLat], [maxLon, maxLat]] = bounds
  const k = Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180))
  const spanX = (maxLon - minLon) * k
  const spanY = maxLat - minLat
  const scale = (width - pad * 2) / spanX
  const height = spanY * scale + pad * 2
  const project = ([lon, lat]: LonLat): [number, number] => [
    pad + (lon - minLon) * k * scale,
    pad + (maxLat - lat) * scale,
  ]
  /** Meters per SVG unit (1° latitude ≈ 111.1 km here), for scale bars. */
  const metersPerUnit = 111_132 / scale
  return { project, width, height, metersPerUnit }
}

export function boundsOf(lines: LonLat[][], padFraction = 0): [LonLat, LonLat] {
  let minLon = Infinity
  let minLat = Infinity
  let maxLon = -Infinity
  let maxLat = -Infinity
  for (const line of lines)
    for (const [lon, lat] of line) {
      minLon = Math.min(minLon, lon)
      maxLon = Math.max(maxLon, lon)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }
  const dLon = (maxLon - minLon) * padFraction
  const dLat = (maxLat - minLat) * padFraction
  return [
    [minLon - dLon, minLat - dLat],
    [maxLon + dLon, maxLat + dLat],
  ]
}

export function pathD(line: LonLat[], project: (p: LonLat) => [number, number], close = false) {
  if (!line.length) return ''
  const pts = line.map(project)
  return `M${pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L')}${close ? 'Z' : ''}`
}

/** Elevation at a course distance, linearly interpolated from the profile. */
export function elevationAt(m: number) {
  const p = course.elevation.profile
  if (m <= p[0][0]) return p[0][1]
  for (let i = 1; i < p.length; i++) {
    if (p[i][0] >= m) {
      const [d0, e0] = p[i - 1]
      const [d1, e1] = p[i]
      return e0 + ((e1 - e0) * (m - d0)) / (d1 - d0)
    }
  }
  return p[p.length - 1][1]
}

const R = 6371008.8
export function haversine([lon1, lat1]: LonLat, [lon2, lat2]: LonLat) {
  const toRad = Math.PI / 180
  const dLat = (lat2 - lat1) * toRad
  const dLon = (lon2 - lon1) * toRad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

const routeCum = (() => {
  const cum = [0]
  for (let i = 1; i < course.route.length; i++) cum.push(cum[i - 1] + haversine(course.route[i - 1], course.route[i]))
  // Scale so the simplified line ends exactly at the leg length.
  const k = course.legM / cum[cum.length - 1]
  return cum.map((c) => c * k)
})()

/** Position on the map at a distance along the full course (out, then back). */
export function pointAtCourse(m: number): LonLat {
  const d = Math.max(0, Math.min(course.legM, m <= course.legM ? m : course.distanceM - m))
  let i = 1
  while (i < routeCum.length - 1 && routeCum[i] < d) i++
  const f = (d - routeCum[i - 1]) / (routeCum[i] - routeCum[i - 1] || 1)
  const a = course.route[i - 1]
  const b = course.route[i]
  return [a[0] + f * (b[0] - a[0]), a[1] + f * (b[1] - a[1])]
}

/** Point inside the polygons that is farthest from any shore: a good spot for a label. */
export function labelPoint(rings: LonLat[][], bounds: [LonLat, LonLat], steps = 36, avoid: { at: LonLat; meters: number }[] = []): LonLat | null {
  const [[x0, y0], [x1, y1]] = bounds
  const k = Math.cos(((y0 + y1) / 2) * (Math.PI / 180))
  const inside = ([x, y]: LonLat) => {
    let hit = false
    for (const ring of rings)
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]
        const [xj, yj] = ring[j]
        if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit
      }
    return hit
  }
  const distToShore = ([x, y]: LonLat) => {
    let best = Infinity
    for (const ring of rings)
      for (let i = 1; i < ring.length; i++) {
        const [ax, ay] = ring[i - 1]
        const [bx, by] = ring[i]
        const dx = (bx - ax) * k
        const dy = by - ay
        const t = Math.max(0, Math.min(1, (((x - ax) * k) * dx + (y - ay) * dy) / (dx * dx + dy * dy || 1)))
        const px = (ax + t * (bx - ax) - x) * k
        const py = ay + t * (by - ay) - y
        best = Math.min(best, px * px + py * py)
      }
    return best
  }
  let best: LonLat | null = null
  let bestD = 0
  for (let i = 1; i < steps; i++)
    for (let j = 1; j < steps; j++) {
      const p: LonLat = [x0 + ((x1 - x0) * i) / steps, y0 + ((y1 - y0) * j) / steps]
      if (!inside(p)) continue
      if (avoid.some((a) => haversine(p, a.at) < a.meters)) continue
      const d = distToShore(p)
      if (d > bestD) {
        bestD = d
        best = p
      }
    }
  return best
}

/**
 * Like makeProjection, but fits the bounds inside width × maxHeight and centers
 * them, so nothing is cropped and the extra space shows surrounding context.
 */
export function fitProjection(bounds: [LonLat, LonLat], width: number, maxHeight: number) {
  const [[minLon, minLat], [maxLon, maxLat]] = bounds
  const k = Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180))
  const spanX = (maxLon - minLon) * k
  const spanY = maxLat - minLat
  const scale = Math.min(width / spanX, maxHeight / spanY)
  const height = Math.min(maxHeight, spanY * scale)
  const offX = (width - spanX * scale) / 2
  const offY = (height - spanY * scale) / 2
  const project = ([lon, lat]: LonLat): [number, number] => [offX + (lon - minLon) * k * scale, offY + (maxLat - lat) * scale]
  return { project, width, height, metersPerUnit: 111_132 / scale }
}
